import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getAllChapters, getAudiosByCategory, getAudiosByCategoryId, getChapterById, getChaptersByEbookId, getChaptersBySeriesId, getChaptersByType, getUniqueChaptersBySeries, getUniqueSeriesByCategory } from '../api/chaptersApi';

export const useChapters = (params = {}) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(['chapters', params], () => getAllChapters(params), {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch chapters';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });

  return {
    chapters: data || [],
    isLoading,
    error,
    refetchChapters: refetch
  };
};

export const useChapterById = (id) => {
  const { setError } = useAuthStore();

  const {
    data: chapter,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['chapter', id],
    () => getChapterById(id),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch chapter details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: id !== undefined && id !== null,
    }
  );

  return {
    chapter,
    isLoading,
    error,
    refetchChapter: refetch
  };
};

export const useChaptersBySeriesId = (seriesId) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['chaptersBySeries', seriesId],
    () => getChaptersBySeriesId(seriesId),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch chapters by series';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: seriesId !== undefined && seriesId !== null,
    }
  );

  return {
    series: data?.series || null,
    chapters: data?.chapters || [],
    isLoading,
    error,
    refetchChapters: refetch
  };
};

export const useChaptersByType = (type, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['chaptersByType', type, page, limit],
    () => getChaptersByType(type, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch chapters by type';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: type !== undefined && type !== null,
    }
  );

  return {
    chapters: data || [],
    isLoading,
    error,
    refetchChapters: refetch
  };
};

export const useChaptersByEbookId = (ebookId) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['chaptersByEbook', ebookId],
    () => getChaptersByEbookId(ebookId),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch chapters by ebook';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: ebookId !== undefined && ebookId !== null,
    }
  );

  return {
    ebook: data?.ebook || null,
    chapters: data?.chapters || [],
    isLoading,
    error,
    refetchChapters: refetch
  };
};

export const useUniqueChaptersBySeries = (type, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['uniqueChaptersBySeries', type, page, limit],
    () => getUniqueChaptersBySeries(type, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch unique chapters by series';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: type !== undefined && type !== null,
    }
  );

  return {
    uniqueSeries: data || [],
    isLoading,
    error,
    refetchUniqueChapters: refetch
  };
};

export const useUniqueSeriesByCategory = (categoryName, categoryType, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesByCategory', categoryName, categoryType, page, limit],
    () => getUniqueSeriesByCategory(categoryName, categoryType, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: categoryName !== undefined && categoryName !== null && 
               categoryType !== undefined && categoryType !== null,
    }
  );

  return {
    series: data?.series || [],
    totalPages: data?.totalPages || 0,
    currentPage: data?.currentPage || page,
    totalCount: data?.totalCount || 0,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useUniqueAudiosByCategory = (categoryName, categoryType, audioType, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['audiosUniByCategory', categoryName, categoryType, audioType, page, limit],
    () => getAudiosByCategory(categoryName, categoryType, audioType, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch audios by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: categoryName !== undefined && categoryName !== null && 
               categoryType !== undefined && categoryType !== null &&
               audioType !== undefined && audioType !== null,
    }
  );

  return {
    audios: data?.audios || [],
    totalPages: data?.totalPages || 0,
    currentPage: data?.currentPage || page,
    totalCount: data?.totalCount || 0,
    isLoading,
    error,
    refetchAudios: refetch
  };
};

export const useUniqueAudiosByCategoryId = (categoryId, audioType, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['audiosUniByCategoryId', categoryId, audioType, page, limit],
    () => getAudiosByCategoryId(categoryId, audioType, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch audios by category ID';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: categoryId !== undefined && categoryId !== null &&
               audioType !== undefined && audioType !== null,
    }
  );

  return {
    audios: data?.audios || [],
    totalPages: data?.totalPages || 0,
    currentPage: data?.currentPage || page,
    totalCount: data?.totalCount || 0,
    isLoading,
    error,
    refetchAudios: refetch
  };
};